import { useAuth } from '@/App';
import { useI18n } from '@/i18n';
import { useBranding } from '@/contexts/BrandingContext';
import { Button } from '@/components/ui/button';
import { ExternalLink, Phone, MapPin, ShieldCheck } from 'lucide-react';

export function AirbnbLink({ property }) {
  const { t } = useI18n();
  if (!property.link) return null;

  return (
    <>
      <a href={property.link} target="_blank" rel="noopener noreferrer" className="block">
        <Button variant="outline" className="w-full border-[#C36F4D] text-[#C36F4D] hover:bg-[#F5E4DE] py-6 text-lg" data-testid="book-airbnb-btn">
          {t('detail.viewAirbnb')}
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </a>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#F5F4F0] rounded-lg mt-2" data-testid="airbnb-trust-badge">
        <ShieldCheck className="w-4 h-4 text-[#264639] flex-shrink-0" />
        <p className="text-xs text-[#57534E]">
          Reserva protegida a través de Airbnb. Garantía de devolución y soporte al viajero incluidos.
        </p>
      </div>
    </>
  );
}

export function WhatsAppContactButton({ property }) {
  const { t } = useI18n();
  const { branding } = useBranding();
  const adminPhone = branding?.social?.whatsapp || branding?.contact?.phone || '+595982188147';

  return (
    <a
      href={`https://wa.me/${adminPhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola! Me interesa la propiedad: ${property.title}${property.code ? ` (${property.code})` : ''}. Quisiera mas información.`)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="block mt-3"
    >
      <Button variant="outline" className="w-full border-[#25D366] text-[#25D366] hover:bg-[#ECFDF5] py-5" data-testid="contact-whatsapp-btn">
        <svg className="w-4 h-4 mr-2 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        {t('detail.contactOwner')}
      </Button>
    </a>
  );
}

export function AdminContactSection({ property }) {
  const { isAdmin } = useAuth();
  const { branding } = useBranding();
  if (!isAdmin) return null;

  const adminEmail = branding?.contact?.email || 'estadia365@gmail.com';
  const adminPhone = branding?.contact?.phone || '+595982188147';

  return (
    <div className="mt-6 pt-6 border-t border-[#E7E5E4]" data-testid="admin-contact-section">
      <p className="text-sm font-medium text-[#1C1917] mb-3">Contacto del administrador</p>
      <div className="space-y-2">
        <a href={`tel:${adminPhone}`} className="flex items-center text-[#57534E] hover:text-[#264639] transition-colors">
          <Phone className="w-4 h-4 mr-2 text-[#264639]" />
          <span className="text-sm">{adminPhone}</span>
        </a>
        <a href={`https://wa.me/${adminPhone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#57534E] hover:text-[#264639] transition-colors">
          <ExternalLink className="w-4 h-4 mr-2 text-[#264639]" />
          <span className="text-sm">WhatsApp</span>
        </a>
        <a href={`mailto:${adminEmail}`} className="flex items-center text-[#57534E] hover:text-[#264639] transition-colors">
          <MapPin className="w-4 h-4 mr-2 text-[#264639]" />
          <span className="text-sm">{adminEmail}</span>
        </a>
      </div>
      {property.contacts && (
        <div className="mt-3 pt-3 border-t border-[#E7E5E4]">
          <p className="text-xs text-[#78716C]">{property.contacts}</p>
        </div>
      )}
    </div>
  );
}
